/* ═══════════════════════════════════════════════════════
   NYC Suite — AI quote extraction (client)
   Talks to Claude only through the admin-gated `claudeProxy`
   Firebase callable — the ANTHROPIC_API_KEY stays server-side.
   Slice 0: callClaude + parseClaudeJSON. Extraction logic
   (SYSTEM_PROMPT, buildRateCard, buildClientHistory,
   extractWithAI, applyAIResult) lands in Slice 1.
   ═══════════════════════════════════════════════════════ */
import { parseClaudeJSON } from "./jsonReply.js";
import { callable } from "../firebase.js";
import { resolveAIStartDiscount, notesAreOperator } from "./aiStartDiscount.js";
import { BILLING_VALUES, isProBono, normalizeBilling, setProBono } from "./proBono.js";
import { RATE_CATALOG, INDUSTRY_LABELS, SESSION_TYPES, DEFAULT_SESSION_TYPE, VOLUME_TIERS, DELIVERABLE_FORMATS, SESSION_FEE_POLICY, SECTION_DEFS, STUDIO_ADDRESS } from "./constants.js";
import { getTierRate, BLANK_SESSION, sortSessions, studioPatch, clearLocationPatch, applyTravelRule, initSections } from "./utils.js";
import { buildContactMatches, isEstimatorCatalogId, normalizeLidarBlocks, cleanGroupLabel, normalizeLineGroups, quoteLineGroups, lineGroupNotesFrom, resolveGroupLabel, calcLineDiscount, stripEmDashes } from "./builderHelpers.js";

// Clamp a model-supplied rate to a finite, non-negative number (mirrors the manual line
// editor's clamp). A negative or non-finite value from a bad model reply becomes 0 rather
// than persisting a negative/NaN charge; a legitimate $0 line stays $0.
const clampRate = (r) => {
  const n = Number(r);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Free text from the model is em-dash-stripped (house style: client documents
// never carry em dashes) and capped so an oversized reply can't bloat the quote
// doc (draft-autosave incident rule, 2026-08-21).
const capText = (s) => stripEmDashes(String(s).trim()).slice(0, 2000);

// Sanitize a model-supplied deposit into the stored shape, or null when invalid.
const sanitizeDeposit = (type, value) => {
  const v = Number(value);
  if (type === "none") return { type: "none", value: null };
  if (type === "percent" && Number.isFinite(v) && v > 0 && v <= 100) return { type: "percent", value: v };
  if (type === "amount" && Number.isFinite(v) && v > 0) return { type: "amount", value: v };
  if (type === "full_session") return { type: "full_session", value: null };
  return null;
};

/**
 * Call Claude via the server proxy.
 * @param {object} opts
 * @param {string} [opts.system] system prompt
 * @param {Array}  opts.messages Anthropic messages array (content may be a
 *   string or an array of content blocks for vision)
 * @param {number} [opts.maxTokens=2000]
 * @param {string} [opts.model] one of the proxy's whitelisted models
 * @returns {Promise<string>} concatenated text of the response
 */
export async function callClaude({ system, messages, maxTokens = 2000, model } = {}) {
  // Match the server's timeoutSeconds (300) — the SDK default of 70s aborts
  // long extractions client-side with deadline-exceeded (seen 2026-08-28 on a
  // dataset-day extraction; PDF/vision runs were exposed to it all along).
  const fn = callable("claudeProxy", { timeout: 300_000 });
  let res;
  try {
    res = await fn({ system, messages, maxTokens, ...(model ? { model } : {}) });
  } catch (e) {
    // Firebase HttpsError → surface a clean message (e.code is available too).
    throw new Error(e?.message || "AI request failed");
  }
  const content = res?.data?.content;
  if (!Array.isArray(content)) throw new Error("Unexpected AI response");
  return content.map((b) => (b?.type === "text" ? b.text : "")).join("");
}

/**
 * Parse a JSON object out of a Claude text response, tolerating markdown
 * fences and any preamble/trailing prose around the object.
 */
export { parseClaudeJSON } from "./jsonReply.js";

/* ═══ EXTRACTION (ported from legacy nyc-suite ai.js; same catalog IDs) ═══ */

const VALID_FORMAT_IDS = new Set(DELIVERABLE_FORMATS.map((f) => f.id));

// Tier dollar lines generated from VOLUME_TIERS so the prompt can't drift from
// the authoritative rates (applyAIResult recomputes via getTierRate regardless).
const tierLine = (catalogId) => (VOLUME_TIERS[catalogId] || []).map((t) => {
  const q = t.min === t.max ? `${t.min}` : (!Number.isFinite(t.max) ? `${t.min}+` : `${t.min}-${t.max}`);
  return `${q}=$${t.rate}`;
}).join(", ");

// Look up a catalog rate by id so prompt rules interpolate LIVE rates instead of hardcoding
// dollar figures that silently drift when a rate changes (Editability constraint, 2026-07-06).
const catRate = (id) => {
  for (const group of Object.values(RATE_CATALOG)) {
    const hit = group.find((c) => c.id === id);
    if (hit) return hit.rate;
  }
  return null;
};

const VOLUME_PRICING_BLOCK = `VOLUME PRICING (do NOT set custom_rate — the tool applies tiers automatically):
- Head scans: ${tierLine("head_scan")}
- Body scans: ${tierLine("body_scan")}
- Just set catalog_id to head_scan or body_scan with the correct qty. The tool will calculate the tier rate.`;

const SYSTEM_PROMPT = `You are an assistant for New York Capture LLC, a 3D scanning and photogrammetry studio in Brooklyn, NYC.

Extract info from emails to build quotes.

SUMMARY RULES:
- The summary describes WHAT THE JOB IS — not what the client asked for.
- Write in complete sentences, professional tone.
- Do NOT include pricing questions, timeline requests, or anything the client asked — only describe the actual project scope.
- Example: "New York Capture will capture 7 full body scans of talent in dynamic poses. Use case is large-scale sculpture reproduction with full color data."

Respond ONLY with JSON (no markdown, no backticks):

{"client_name":"","contact_name":"","production_name":"","job_location":"","scan_date":"","scan_date_tbd":false,"multi_day":false,"sessions":[{"date":"","session_type":"capture","location":"","notes":""}],"summary":"","doc_type":"quote|rate_sheet|estimate","industry_guess":"film_tv|commercial_product|music|fashion|gaming|spatial|museum_archival|visual_art|gallery|engineering_manufacturing|architecture_real_estate|education_research_institutional|medical|theatre|other","industry_confidence":"high|medium|low","is_onlocation":false,"lidar_detected":false,"drone_detected":false,"lidar":[{"label":"","note":"","mode":null,"day_type":null,"crew_size":null,"photogrammetry":null,"high_density":null,"hours_on_site":null,"overtime_hrs":null,"setups":null,"rush":null,"days":null,"evidence":{}}],"suggested_line_items":[{"catalog_id":"","qty":null,"custom_description":null,"custom_notes":null,"custom_rate":null,"group":null}],"turnaround":"3-5","deliverable_text":null,"deliverable_formats":[],"deposit_type":null,"deposit_value":null,"project_terms":null,"data_delivery_note":null,"no_processing":false,"quote_discount":null,"free_work":false,"questions_for_user":[],"lead_source_guess":"referral|website|cold|repeat|other","extracted_contacts":[{"name":"","email":"","url":"","role":"contact","source":"email_from"}],"notes":""}

Catalog IDs:
Sessions: half_art, full_art, partial_mobile, full_onlocation
Scanning: head_scan, body_scan, preview_model
Leo (handheld scanner): leo_body, leo_head, leo_hands, leo_torso, leo_other
Low Poly Deliverable: lowpoly_session, lowpoly_body
- Use the Low Poly items ONLY when the client explicitly requests a raw full-body mesh for a CLO3D, virtual garment fitting, fit-avatar, or similar low-poly workflow AND accepts an uncleaned OBJ/FBX of up to 1M polygons. Always quote the pair together: lowpoly_session (1 hour studio window) + lowpoly_body (raw reconstructed model). Cleanup, decimation, and retopology are never included. Do not infer this bracket from a generic request for a body scan, low-resolution preview, optimized/game-ready model, base mesh, target polygon count, or decimation-ready asset. If the requested finish or production-readiness requirement is ambiguous, add a question to questions_for_user instead of selecting Low Poly items. NEVER substitute Low Poly items for a standard head_scan or body_scan request, and never add a half_art or full_art session fee to a Low Poly quote.
Objects: obj_jewelry_std, obj_jewelry_mod, obj_jewelry_cpx, obj_utensil_std, obj_utensil_mod, obj_utensil_cpx, obj_shoe_std, obj_shoe_mod, obj_shoe_cpx, obj_basketball_std, obj_basketball_mod, obj_basketball_cpx, obj_suitcase_std, obj_suitcase_mod, obj_suitcase_cpx, obj_chair_std, obj_chair_mod, obj_chair_cpx, obj_motorcycle_std, obj_motorcycle_mod
Environments: env_portalcam, env_nimble_onloc, env_onloc_full
Addons: extra_captures, additional_processing, advanced_setup, truck, travel_local, travel_mid
- truck is the travel fee for jobs where the client does not provide transport. All film and TV jobs REQUIRE production transport through teamsters (cube truck with a lift gate, 20 ft minimum), billed directly to the production. Only add truck to a film/TV quote if the email states the production CANNOT provide transport; note it as "Travel fee (transport not provided)". The fee is a minimum and may be adjusted for further locations.

Object complexity: std=Standard (simple geometry), mod=Detailed (fine surface detail or texture), cpx=Complex (organic, reflective, or multi-tool capture). Size tiers: under 3in, 3-8in, 8-14in, 14-24in, 2-4ft, 3-5ft, 5-7ft.

LiDAR (terrestrial, Leica RTC 360): if the request involves LiDAR scanning (laser scanning, point cloud, LiDAR + processed geometry), set "lidar_detected": true. Do NOT emit a priced suggested_line_item for LiDAR, and do NOT substitute a photogrammetry session line to represent LiDAR site time. LiDAR is priced separately by the LiDAR estimator (crew, day length, setups), which the operator confirms. Never guess a LiDAR price. Instead fill "lidar" with one entry per LiDAR BLOCK, so the estimator opens pre-filled for each. A block is a scope priced on its own: a location, a set piece, or a day the text describes separately (a backlot set, "the Pile", a tent interior, a street intersection). One scope means one entry. When the operator's notes list blocks ("Block: Pile. Note: ... capture + processing, film day, 2 crew on site ..."), follow that list exactly, one entry per block, in that order. For each entry:
- label: the short name the text uses for that scope, two to four words ("Pile", "Fresh Kills tent"). "" when there is a single unlabeled scope.
- note: an optional heading line for that block copied from the text, such as its date or place ("Wednesday, October 7"). "" when none is stated.
Then the facts below, each null unless the text states it FOR THAT BLOCK:
- mode: "processing" when a registered point cloud, processed geometry, or models are wanted; "captureOnly" when the text says capture only, archive only, or no processing; "processingOnly" when the data was already captured and only processing is being asked for (deferred processing, a change order to process an earlier scan).
- day_type: "film" for a film or TV production (10-hour day), "standard" otherwise (8-hour day). Only when the production type is clear.
- crew_size: the number of New York Capture crew stated for the LiDAR day ("3 on staff", "two operators"). Never infer it from the job size.
- photogrammetry: true when camera photogrammetry, a photo pass, or photo textures are requested alongside the LiDAR; false only when the text says LiDAR only.
- high_density: true only when high-density or high-resolution scans are explicitly requested.
- hours_on_site: the hours on site when stated ("on site for the full 10-hour shoot" is 10).
- overtime_hrs: only when overtime hours are stated explicitly.
- setups: the number of scan positions or setups when stated; for a range use the top of the range ("25-35 setups" is 35). Never estimate it from square footage or a description.
- rush: true when processed delivery is required within 2 business days of capture, over a weekend, or the text says rush or expedite.
- days: the number of LiDAR capture days when stated ("two days on set" is 2). Leave null for a single day or when unstated.
- evidence: for every non-null field above, the exact phrase you took it from, copied from the text, at most 15 words, keyed by the same field name ("setups": "25-35 setups", "crew_size": "3 on staff"). Never paraphrase and never write evidence for a null field.
Put the remaining LiDAR scope (subject, indoor or outdoor, what is being delivered) in the notes field. Never emit a LiDAR block as a line item.

Vehicles (cars, trucks, motorcycles above 7 ft, boats): there is no catalog vehicle rate. Set "lidar_detected": true, put the vehicle scope in notes, and do NOT emit a priced line item for the vehicle. It is priced by the operator via the LiDAR estimator like any large-scale capture.

Drone (DJI Mavic 3E, aerial photogrammetry): set "drone_detected": true whenever drone or aerial work is in scope; drone capture and drone processing are never priced by you. Do NOT emit a suggested_line_item for a drone flight, aerial capture, drone photogrammetry, an aerial or matchmove model built from drone stills, or a drone permit, and do NOT substitute an on-location session line for drone site time. Drone work is priced by the drone estimator (crew, day length, acres flown, geometry only or textured), which the operator opens and confirms; the Drone permit coordination line is added by the operator from the catalog. Put the drone scope in notes: the site, the acreage or dimensions when stated, and whether a textured mesh or geometry only is wanted. An orthomosaic or stills-only deliverable is a custom line the operator prices by hand: describe it in notes and add a question to questions_for_user instead of pricing it.

SCHEDULE EXTRACTION RULES (scan_date / multi_day / sessions):
- Match the day-count mentioned in the email. Never invent sessions the email didn't mention.
- Single day → multi_day: false, one entry in sessions with scan_date filled, scan_date_tbd: false.
- Multiple days → multi_day: true, one session entry per day, scan_date = first session date.
- Dates vague ("next month", "late May", "after their opening") → leave scan_date and session date empty, set scan_date_tbd: true. Still populate the session count from the email.
- session_type values: "capture" (scanning, shoot, photo day), "setup" (load in, prep, advance day), "strike" (teardown, wrap, load out), "flight" (travel day, fly in/out). Default to "capture" if unclear.
- Never add a strike/setup/flight session unless the email mentions it.
- session location: set each session's "location" to the venue/address named for THAT day — different days can be different places. Use "studio" for the Brooklyn studio. Leave "" to inherit the job's main location (so a single-venue shoot needn't repeat it per day).

IMPORTANT SESSION LINE-ITEM RULES:
- Standard studio session fees apply ONLY to camera-array captures (head_scan, body_scan — the Big Rig / Head Rig). ALWAYS include a half_art or full_art session alongside those standard array scan items. The dedicated Low Poly bracket is the exception: use lowpoly_session + lowpoly_body together, with no half_art or full_art line.
- Handheld equipment has NO session fee: Leo scans (leo_*) and object photogrammetry (obj_*) are all-in per-scan rates. A studio visit for handheld or object work only gets NO session line item.
- A LiDAR-only job also gets NO session line item — the LiDAR estimator covers the site time.
- Studio sessions: $${catRate("half_art")} partial (half_art) / $${catRate("full_art")} full (full_art). ONE standard rate for every industry — there are no film/TV session rates.
- On-location camera array (Big Rig / Head Rig): full_onlocation=$${catRate("full_onlocation")}
- On-location handheld/object jobs (NOT camera array): include env_nimble_onloc for up to 3 hrs on site ($${catRate("env_nimble_onloc")}) or env_onloc_full for 3-8 hrs ($${catRate("env_onloc_full")}), plus the per-scan/per-object lines for the deliverables. These session bands are minimums; deliverables always bill on top. Exception: a single object or single scan at a LOCAL site is a drop-in visit (travel plus the per-object/per-scan rate, subject to a local drop-in minimum) — do NOT add a session line; include the per-object/per-scan line, put "local drop-in visit" in notes, and let the operator confirm the total.
- Partial day = up to 2 hours (a quick 1-2 scan visit). Full day = 8 hours and carries a 4 processed model minimum. When in doubt, choose the full day.

${VOLUME_PRICING_BLOCK}

PRODUCTION NAME (production_name):
- The show/project/production codename, if one is VISIBLY present in the email — a subject-line codename ("TGA4 // Cyberscanning" → "TGA4"), or an explicitly labeled "Production:", "Show:", "Project:", or working title.
- Copy only text you can see. NEVER infer the studio or network behind a codename, and never use the sender's company as the production.
- This is separate from client_name: client_name is who is paying (the company or production LLC); production_name is what the project is called. When unsure, leave production_name "".

CONTACT EXTRACTION (extracted_contacts):
- List every person and company named in the email so they can be matched to the CRM.
- For each: name, email (if present), url/domain (if present), and role.
- role values: "contact" (the main person), "client" (the company), "cc" (copied), "coordinator", "other".
- source: where you saw them — "email_from", "email_cc", "email_signature", or "email_body".
- Do not invent contacts. Skip New York Capture's own staff (newyorkcapture.com / nycap3d.com addresses).

CUSTOM-PRICED JOBS:
- When the operator's notes spell out explicit line items with their own rates, follow them EXACTLY: emit custom lines (catalog_id "", custom_description, custom_rate, qty as stated) and do not re-map them to catalog items, even when a description resembles a catalog item.

LINE GROUPS (group on suggested_line_items):
- A quote can print its lines under headings, one per LiDAR block or location, each with a subtotal. When the operator's notes assign a line to a block or group ("group Pile", "travel_mid, group Fresh Kills"), set that line's "group" to the exact label used. Use the same label the matching LiDAR block carries. Otherwise leave group null. Never invent a group.

NEGOTIATED TERMS & OVERRIDES (all evidence-grounded — copy only what is explicitly stated, never invent):
- deposit_type/deposit_value: set ONLY when a deposit is explicitly stated. "percent" with deposit_value 1-100 for a percentage deposit; "amount" with the dollar figure for a fixed deposit; "full_session" when a 100%-of-session-rate deposit is requested. Otherwise both stay null.
- project_terms: operational terms explicitly negotiated (overage rates, overtime, attendance or no-show pricing, capture allowances), written as complete sentences. null when none are stated.
- data_delivery_note: set ONLY when a specific delivery method is stated (client-supplied drive, FTP, etc.), as one or two complete sentences. null otherwise.
- no_processing: true ONLY when the scope is explicitly capture-only or raw-source-data-only, with no reconstruction, models, or processing delivered.
- deliverable_formats: ids from [obj, fbx, usdz, glb, splat, pointcloud, stl, raw_photos] matching the stated deliverables (e.g. raw source files only → ["raw_photos"]). Leave [] when formats are not stated.
- Never use em dashes in any text you output; use commas or periods.

QUOTE DISCOUNT (quote_discount):
- Set it ONLY from the INTERNAL NOTES, never from the client email. A client asking for a discount or a lower price is a question for the operator, not a discount: put it in questions_for_user.
- "Quote discount: 15%" gives {"type":"percent","value":15}. "Quote discount: $500" gives {"type":"amount","value":500}. "Discount to a total of $1,000", or a flat total the notes say was agreed for the whole job, gives {"type":"target_total","value":1000}. value is a plain number. Otherwise null.
- Report only the intent and the number. Never compute a discount amount yourself: the tool works out the dollars needed to reach a target total.
- A discount never changes a line. Every line keeps its standard catalog rate: never lower a rate, set a custom_rate, change a quantity, or drop a line to reach a discounted or flat total. The quote discount is what lowers the total.
- Pro bono is not a discount. When the notes say the WHOLE job is pro bono, free, or not charged, set "free_work": true and leave quote_discount null; the operator turns on Pro bono in the builder. A single free item (free parking, no charge for travel) or a client asking about pro bono is not free work: free_work stays false.

If the job is at the New York Capture studio, set job_location to "studio".`;

/* Build a rate card from historical quotes for AI pricing context. */
export function buildRateCard(savedQuotes) {
  const byIndustry = {};
  (savedQuotes || []).forEach((q) => {
    if (q.jobStatus === "lost") return;
    const ind = q.industry || "other";
    if (!byIndustry[ind]) byIndustry[ind] = { count: 0, totalRev: 0, items: {} };
    byIndustry[ind].count++;
    (q.lineItems || []).forEach((li) => {
      const desc = li.description || li.catalogId || "unknown";
      if (!byIndustry[ind].items[desc]) byIndustry[ind].items[desc] = { rates: [], qtys: [] };
      byIndustry[ind].items[desc].rates.push(Number(li.rate) || 0);
      byIndustry[ind].items[desc].qtys.push(Number(li.qty) || 1);
      byIndustry[ind].totalRev += (Number(li.rate) || 0) * (Number(li.qty) || 1);
    });
  });

  if (Object.keys(byIndustry).length === 0) return "";

  let card = "\n\nHISTORICAL RATE CARD (from past quotes — use to inform pricing):\n";
  Object.entries(byIndustry).forEach(([ind, data]) => {
    const label = INDUSTRY_LABELS[ind] || ind;
    card += `\n${label} (${data.count} quotes, $${Math.round(data.totalRev).toLocaleString()} total):\n`;
    Object.entries(data.items).forEach(([desc, info]) => {
      const minR = Math.min(...info.rates);
      const maxR = Math.max(...info.rates);
      const avgQ = Math.round(info.qtys.reduce((a, b) => a + b, 0) / info.qtys.length);
      card += `  ${desc}: $${minR}${minR !== maxR ? `-$${maxR}` : ""} (avg qty: ${avgQ})\n`;
    });
  });
  return card;
}

const PERSONAL_EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "aol.com", "icloud.com"];
const WON_STATUSES = ["booked", "completed", "invoiced", "paid"];
const SIMILAR_INDUSTRIES = {
  film_tv: ["film_tv", "commercial_product", "theatre"],
  commercial_product: ["commercial_product", "fashion", "film_tv"],
  music: ["music", "commercial_product", "film_tv"],
  fashion: ["fashion", "commercial_product"],
  gaming: ["gaming", "spatial", "film_tv"],
  spatial: ["spatial", "gaming", "engineering_manufacturing"],
  museum_archival: ["museum_archival", "visual_art", "gallery"],
  visual_art: ["visual_art", "gallery", "museum_archival"],
  gallery: ["gallery", "visual_art", "museum_archival"],
  engineering_manufacturing: ["engineering_manufacturing", "commercial_product", "spatial"],
  architecture_real_estate: ["architecture_real_estate", "engineering_manufacturing"],
  education_research_institutional: ["education_research_institutional", "museum_archival"],
  medical: ["medical", "engineering_manufacturing"],
  theatre: ["theatre", "film_tv"],
  other: [],
};

/* Build client-specific + comparable-quote history for the prompt. crmRecords =
   combined contacts + clients. */
export function buildClientHistory(emailText, savedQuotes, jobs, crmRecords) {
  const emailLower = (emailText || "").toLowerCase();

  const matchedRecords = (crmRecords || []).filter((r) => {
    if (!r.name || r.name.length < 3) return false;
    return emailLower.includes(r.name.toLowerCase());
  });

  const emailMatch = emailLower.match(/from:?\s*[^<]*<?([a-z0-9._%+-]+@([a-z0-9.-]+\.[a-z]{2,}))/);
  const senderDomain = emailMatch ? emailMatch[2] : "";
  if (senderDomain && !PERSONAL_EMAIL_DOMAINS.includes(senderDomain)) {
    const domainMatches = (crmRecords || []).filter((r) =>
      (r.orgDomain || "") === senderDomain ||
      (r.email && r.email.endsWith("@" + senderDomain)) ||
      (r.emails && r.emails.some((em) => em.endsWith("@" + senderDomain)))
    );
    domainMatches.forEach((r) => { if (!matchedRecords.find((m) => m.id === r.id)) matchedRecords.push(r); });
  }

  const clientNames = new Set(matchedRecords.map((e) => (e.name || "").toLowerCase()));
  const clientCompanies = new Set(matchedRecords.map((e) => (e.company || "").toLowerCase()).filter(Boolean));

  const clientQuotes = (savedQuotes || []).filter((q) => {
    const cn = (q.clientName || "").toLowerCase();
    const ct = (q.contactName || "").toLowerCase();
    return clientNames.has(cn) || clientNames.has(ct) || clientCompanies.has(cn);
  });
  const clientJobs = (jobs || []).filter((j) => {
    const cn = (j.clientName || "").toLowerCase();
    const ct = (j.contactName || "").toLowerCase();
    return clientNames.has(cn) || clientNames.has(ct) || clientCompanies.has(cn);
  });

  const isReturningClient = clientQuotes.length > 0 || clientJobs.length > 0;
  const detectedIndustry = matchedRecords.find((e) => e.industry)?.industry || "";

  let history = "";

  if (isReturningClient) {
    history += "\n\nRETURNING CLIENT — FULL HISTORY:\n";
    history += `Client: ${matchedRecords[0]?.name || "Unknown"}${matchedRecords[0]?.company ? ` (${matchedRecords[0].company})` : ""}\n`;
    if (detectedIndustry) history += `Industry: ${detectedIndustry}\n`;

    if (clientQuotes.length > 0) {
      const sorted = [...clientQuotes].sort((a, b) => (b.quoteDate || "").localeCompare(a.quoteDate || ""));
      history += `\nAll Quotes for This Client (${sorted.length}):\n`;
      sorted.forEach((q) => {
        const status = q.jobStatus || "quoted";
        const won = WON_STATUSES.includes(status);
        history += `  ${q.quoteDate || "?"} — ${q.docType || "quote"} — $${q.total || 0} [${status}]${won ? " ✓ WON" : ""}\n`;
        (q.lineItems || []).forEach((li) => {
          const hasQty = li.qty != null && li.qty > 0;
          history += `    ${li.description}: ${hasQty ? `qty ${li.qty}, ` : ""}rate $${li.rate}${hasQty ? `, total $${Math.round(li.qty * li.rate)}` : ""}\n`;
        });
      });
    }

    if (clientJobs.length > 0) {
      const sorted = [...clientJobs].sort((a, b) => (b.captureDate || "").localeCompare(a.captureDate || ""));
      history += `\nCompleted Jobs (${sorted.length}):\n`;
      sorted.forEach((j) => {
        history += `  ${j.captureDate || "?"} — ${j.description?.slice(0, 100) || "No description"} — $${j.grossRevenue || 0} [${j.status}]\n`;
      });
    }

    history += "\nPRICING INSTRUCTION: This is a returning client. Use their history to understand SCOPE and recurring needs, and as the pricing reference for CUSTOM (non-catalog) items. Catalog line items ALWAYS use the current catalog rates listed above — never carry a catalog item's rate from a past quote; rates change deliberately (the old film/TV session rates are retired).\n";
  }

  const clientQuoteIds = new Set(clientQuotes.map((q) => q.id));
  const allOtherQuotes = (savedQuotes || []).filter((q) => !clientQuoteIds.has(q.id) && q.jobStatus !== "lost" && q.lineItems?.length > 0);
  const targetIndustry = detectedIndustry || "";
  const relatedIndustries = new Set(SIMILAR_INDUSTRIES[targetIndustry] || [targetIndustry]);

  const scoredQuotes = allOtherQuotes.map((q) => {
    let score = 0;
    const qInd = q.industry || "other";
    if (qInd === targetIndustry) score += 100;
    else if (relatedIndustries.has(qInd)) score += 50;
    else score += 10;
    if (q.quoteDate) {
      const age = (Date.now() - new Date(q.quoteDate).getTime()) / (1000 * 60 * 60 * 24);
      if (age < 30) score += 40;
      else if (age < 90) score += 30;
      else if (age < 180) score += 20;
      else if (age < 365) score += 10;
    }
    if (WON_STATUSES.includes(q.jobStatus || "quoted")) score += 25;
    return { ...q, _score: score };
  }).sort((a, b) => b._score - a._score);

  const relevantQuotes = scoredQuotes.slice(0, 15);
  if (relevantQuotes.length > 0) {
    history += `\n\n${isReturningClient ? "COMPARABLE" : "REFERENCE"} QUOTES FROM SIMILAR PROJECTS (${relevantQuotes.length} most relevant):\n`;
    history += "Use these as pricing context. More recent quotes and won quotes are more reliable references.\n\n";
    relevantQuotes.forEach((q) => {
      const status = q.jobStatus || "quoted";
      const won = WON_STATUSES.includes(status);
      const ind = q.industry || "other";
      history += `  ${q.quoteDate || "?"} — ${q.clientName || "?"} [${ind}] — $${q.total || 0} [${status}]${won ? " ✓ WON" : ""}\n`;
      (q.lineItems || []).forEach((li) => {
        const hasQty = li.qty != null && li.qty > 0;
        history += `    ${li.description}: ${hasQty ? `qty ${li.qty}, ` : ""}rate $${li.rate}${hasQty ? `, total $${Math.round(li.qty * li.rate)}` : ""}\n`;
      });
    });
    if (!isReturningClient) {
      history += "\nPRICING INSTRUCTION: This is a new client. Use the reference quotes above to determine fair market rates for this type of work. Prioritize recent won quotes from the same industry. You have flexibility to adjust rates based on project complexity and scope.\n";
    }
  }

  return history;
}

/* Today's date, injected so relative phrases ("next Tuesday") resolve. */
const todayHeader = () => `Today's date: ${new Date().toISOString().slice(0, 10)}\n\n`;

/* A result is usable only if it carries real signal (F6) — guards against a
   valid-but-empty/garbage object silently advancing to an empty builder. */
export function isMeaningfulExtraction(p) {
  if (!p || typeof p !== "object") return false;
  const hasItems = Array.isArray(p.suggested_line_items) && p.suggested_line_items.length > 0;
  const hasIdentity = !!(p.client_name || p.contact_name);
  const hasSummary = !!(p.summary && String(p.summary).trim());
  return hasItems || hasIdentity || hasSummary;
}

/* Compose the email/notes text the model reads. (Notes override the email.) */
export function buildExtractionText({ emailText, notesText } = {}) {
  return notesText && notesText.trim() ?
    `${emailText || ""}\n\nINTERNAL NOTES (from operator — trust over the email if they conflict):\n${notesText}` :
    (emailText || "");
}

/* Build the Anthropic message `content` for an extraction (Slice 3).
   Text-only → a plain string (cheap). With readable attachments → an array of
   content blocks: the text, then one image/document block per file. Files must
   already be resolved to base64 (see attachmentBytes.js) — this stays pure so
   it's unit-testable. `kind` is "image" or "pdf"; anything else is ignored
   (Claude can't read .doc/.docx natively). */
/* ═══ ONE-BOX BRIEF ═══
   The AI Start screen's "One brief" mode takes the whole Claude-written brief in a
   single box and splits it here into the two fields everything else already reads:
   the client communication (kept as emailText so the CRM sender match and the
   "trust the notes over the email" framing keep working) and the operator notes.
   The divider is a line that says YOUR NOTES (also NOTES / INTERNAL NOTES /
   OPERATOR NOTES, any case, with or without dashes or a colon). The LAST such line
   wins, so a quoted email that happens to contain a "Notes:" line does not split
   early. A leading CLIENT COMMUNICATION header line is dropped. No divider → the
   whole paste is client communication and notes stay empty. */
const NOTES_DIVIDER = /^\s*(?:[-=#*_>]+\s*)?(?:your|internal|operator|my)?\s*notes\s*(?:[-=#*_:]+\s*)?$/i;
const EMAIL_HEADER = /^\s*(?:[-=#*_>]+\s*)?client\s+communication(?:\s*\([^)]*\))?\s*(?:[-=#*_:]+\s*)?$/i;

export function splitBrief(raw) {
  const text = String(raw ?? "").replace(/\r\n?/g, "\n");
  const lines = text.split("\n");
  let cut = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (NOTES_DIVIDER.test(lines[i])) { cut = i; break; }
  }
  let emailLines = cut >= 0 ? lines.slice(0, cut) : lines;
  const notesLines = cut >= 0 ? lines.slice(cut + 1) : [];
  // Drop a leading "CLIENT COMMUNICATION" header (and blank lines around it).
  let k = 0;
  while (k < emailLines.length && !emailLines[k].trim()) k++;
  if (k < emailLines.length && EMAIL_HEADER.test(emailLines[k])) emailLines = emailLines.slice(k + 1);
  return {
    emailText: emailLines.join("\n").trim(),
    notesText: notesLines.join("\n").trim(),
    split: cut >= 0,
  };
}

/* Whether the notes splitBrief takes from a One brief paste are certainly the
   operator's: the divider it cut at names them (YOUR / INTERNAL / OPERATOR / MY
   NOTES). A bare "Notes" line may be part of the client's own email, so text under
   it may not move money: AI Start reads quote-discount lines only from notes this
   returns true for (or from the separate Your Notes box). Same divider rule and
   same "last one wins" as splitBrief. */
const OPERATOR_NOTES_DIVIDER = /^\s*(?:[-=#*_>]+\s*)?(?:your|internal|operator|my)\s*notes\s*(?:[-=#*_:]+\s*)?$/i;
export function briefNotesAreOperator(raw) {
  const lines = String(raw ?? "").replace(/\r\n?/g, "\n").split("\n");
  for (let i = lines.length - 1; i >= 0; i--) {
    if (NOTES_DIVIDER.test(lines[i])) return OPERATOR_NOTES_DIVIDER.test(lines[i]);
  }
  return false;
}

/* Where the quote's notes came from, kept on the quote as `notesSource` so a reopen
   or a mode switch cannot promote them: both rebuild the One brief paste with
   joinBrief, which writes a YOUR NOTES line. Only "operator" is trusted for an AI
   Start discount line: notes typed in the Your Notes box, or under a YOUR NOTES style
   line in One brief. "bare_divider" = cut from a One brief paste at a bare "Notes"
   line; "prospect" = a website inquiry's text (prospectQuote.js); no field = notes
   saved before 2026-10-09, origin unknown. Empty notes carry nothing, so they count
   as the operator's. On a One brief edit, untrusted notes that did not change keep
   their origin; changed notes take the divider they now sit under. Pure; tested. */
export { notesAreOperator };
export function nextNotesSource(prevSource, prevNotes, raw) {
  const { notesText } = splitBrief(raw);
  if (!notesText) return "operator";
  const prevTrusted = notesAreOperator({ notesSource: prevSource, notesText: prevNotes });
  if (!prevTrusted && notesText === String(prevNotes ?? "").trim()) return prevSource || "unknown";
  return briefNotesAreOperator(raw) ? "operator" : "bare_divider";
}

// The inverse, used to seed the one-box view from a quote that already holds both
// fields (a reopened draft, or a switch from two boxes to one).
export function joinBrief({ emailText, notesText } = {}) {
  const e = String(emailText ?? "").trim();
  const n = String(notesText ?? "").trim();
  if (!n) return e;
  return `${e ? e + "\n\n" : ""}YOUR NOTES\n${n}`;
}

export function buildExtractionContent({ emailText, notesText, attachments = [] } = {}) {
  const userText = buildExtractionText({ emailText, notesText });
  const files = (attachments || []).filter((a) => a && a.b64 && (a.kind === "image" || a.kind === "pdf"));
  if (files.length === 0) return userText;

  const blocks = [];
  if (userText.trim()) blocks.push({ type: "text", text: userText });
  for (const f of files) {
    blocks.push({ type: "text", text: `[Attached ${f.kind === "pdf" ? "PDF" : "image"}: ${f.name || "file"}]` });
    if (f.kind === "image") {
      blocks.push({ type: "image", source: { type: "base64", media_type: f.mediaType || "image/jpeg", data: f.b64 } });
    } else {
      blocks.push({ type: "document", source: { type: "base64", media_type: "application/pdf", data: f.b64 } });
    }
  }
  // Guarantee at least one substantive text instruction when only files were given.
  if (!blocks.some((b) => b.type === "text" && b.text.length > 20)) {
    blocks.unshift({ type: "text", text: "Extract the quote details from the attached file(s)." });
  }
  return blocks;
}

/**
 * Run AI extraction on pasted email/notes text (+ optional attachments, Slice 3).
 * @returns {Promise<object>} parsed extraction JSON (feed to applyAIResult)
 */
/* The exact request extractWithAI sends, without sending it. Exported so a live
   prompt check (src/lib/lidarLive.test.js) can hit the model with the real system
   prompt and content shape outside the admin-gated proxy. */
export function buildExtractionRequest({ emailText, notesText, savedQuotes, jobs, crmRecords, attachments = [], customPrompt } = {}) {
  const rateCard = buildRateCard(savedQuotes);
  const clientHistory = buildClientHistory(emailText, savedQuotes, jobs, crmRecords);
  const system = todayHeader() + (customPrompt || SYSTEM_PROMPT) + rateCard + clientHistory;

  const content = buildExtractionContent({ emailText, notesText, attachments });
  const hasFiles = Array.isArray(content); // file blocks → more headroom for vision/PDF

  // 2000 tokens proved too small for long pasted emails — the reply got cut off
  // mid-array and JSON.parse threw a raw "Expected ']'" at the operator
  // (2026-07-17). max_tokens is a ceiling, not a spend, so a generous budget
  // costs nothing on short replies. The proxy clamps at 16384.
  return { system, messages: [{ role: "user", content }], maxTokens: hasFiles ? 8000 : 6000 };
}

export async function extractWithAI(opts = {}) {
  const text = await callClaude(buildExtractionRequest(opts));
  return finishExtraction(text);
}

/* shared tail: parse + guard. */
export function finishExtraction(text) {
  let parsed;
  try {
    parsed = parseClaudeJSON(text);
  } catch {
    // The proxy doesn't surface stop_reason, so an unparseable reply is most
    // likely a truncated one — say that instead of the raw JSON.parse error.
    throw new Error("The AI reply couldn't be read — it may have been cut off. Try Extract again, or trim the pasted email down to the relevant part.");
  }
  if (!isMeaningfulExtraction(parsed)) {
    throw new Error("The AI couldn't pull a quote from that. Add more detail to the email or notes, or use Skip to fill it in manually.");
  }
  return parsed;
}

/* Map an extraction result onto a quote object (catalog match + tier rates +
   session hydration + studio detection). Returns a new quote at step "build".
   `crm.contacts`/`crm.clients` feed the CRM contact-match review (Slice 2);
   omit them and contactMatches simply stays empty.
   `notesText` is the YOUR NOTES box: a quote discount stated there ("Quote
   discount: 15%", "Quote discount: $500", "Discount to a total of $1,000") is
   applied by resolveAIStartDiscount (aiStartDiscount.js) after the lines are
   priced. Without one, the quote's discounts are left exactly as they were. */
export function applyAIResult(parsed, quote, opts = {}) {
  return applyAIResultWithNotice(parsed, quote, opts).quote;
}

/* The same mapping, plus the one-line discount notice the builder shows after
   AI Start ("" when the notes asked for no discount). The notice is also
   appended to aiNotes, the extraction's counterpart of refine's notes_for_user. */
export function applyAIResultWithNotice(parsed, quote, { contacts = [], clients = [], notesText = "", notesTrusted = true } = {}) {
  const allItems = [
    ...RATE_CATALOG.sessions, ...RATE_CATALOG.scanning, ...RATE_CATALOG.leo,
    ...RATE_CATALOG.lowpoly, ...RATE_CATALOG.objects, ...RATE_CATALOG.environments,
    ...RATE_CATALOG.addons,
  ];
  // LiDAR and drone work are priced ONLY by their estimators — drop any lidar_estimate or
  // drone_estimate id the model may still emit so it can't become a $0 line (no catalog
  // match → rate 0) that triggers an addendum.
  // Retired ids map to their current equivalents so a stale model emission can't become
  // a silent $0 "Custom Line Item" (film/TV session tier retired 2026-07-01; one standard rate).
  const RETIRED_IDS = {
    half_filmtv: "half_art", full_filmtv: "full_art",
    // Body parts are handheld/Leo at $300; the old rig "scanning" ids were retired 2026-07-06.
    torso_scan: "leo_torso", other_body: "leo_other",
  };
  // Vehicles have no catalog rate (obj_car retired 2026-07-06); route to the LiDAR estimator
  // like any large-scale capture — drop the line here and raise lidarDetected on the return.
  const rawItems = parsed.suggested_line_items || [];
  const hadVehicle = rawItems.some((si) => si.catalog_id === "obj_car");
  // LiDAR prefill: facts only (setups, crew, hours, rush), one block per scope, sanitized
  // through the one shared normalizer (a single object from an older model reply becomes a
  // one-block list). A stated fact is enough to flag LiDAR scope even if the model forgot
  // lidar_detected. Absent → whatever the quote already carried survives, EXCEPT when the
  // model explicitly reports no LiDAR: then a prefill left over from an earlier extraction
  // of this quote is cleared so stale "brief" facts can't resurface.
  let lidarPrefill = normalizeLidarBlocks(parsed.lidar);
  const lineItems = rawItems
    .filter((si) => !isEstimatorCatalogId(si.catalog_id) && si.catalog_id !== "obj_car")
    .map((si) => {
    const catalogId = RETIRED_IDS[si.catalog_id] || si.catalog_id;
    const cat = allItems.find((c) => c.id === catalogId);
    // F2: preserve an intentional null qty (rate sheet) and a legitimate $0 custom
    // rate instead of clobbering them; only default qty when the key is absent.
    // F7: coerce a numeric-string qty ("5") to a number so tiered rates still apply.
    const rawQty = si.qty === undefined ? 1 : si.qty;
    const qty = (typeof rawQty === "string" && rawQty.trim() !== "" && !Number.isNaN(Number(rawQty)))
      ? Number(rawQty)
      : rawQty;
    // Catalog items ALWAYS price from the current catalog — the model may not carry rates
    // from client history (Codex 2026-07-02: a returning film client's retired $3,600
    // session resurfaced via custom_rate). custom_rate applies to custom lines only.
    let rate = cat ? cat.rate : clampRate(si.custom_rate ?? 0);
    if (cat?.tiered && typeof qty === "number" && qty > 0) rate = getTierRate(cat.id, qty);
    rate = clampRate(rate);
    const groupLabel = cleanGroupLabel(si.group);
    return {
      key: `li_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      catalogId: catalogId || "custom",
      description: si.custom_description || (cat ? cat.label : "Custom Line Item"),
      qty,
      rate,
      notes: si.custom_notes || (cat?.notes || ""),
      isCustomRate: !cat && si.custom_rate != null,
      discount: null,
      // The group the operator's notes assigned (PDF heading + subtotal); absent otherwise.
      ...(groupLabel ? { group: groupLabel } : {}),
    };
  });

  // Session fees apply ONLY to camera-array capture (Big Rig / Head Rig) — handheld
  // and object per-scan rates are all-in (Ivin 2026-07-03). The old guard stripped
  // studio sessions unless a head_scan/body_scan line existed, which deleted the
  // legitimate session fee on a custom-priced rig day (Nonverbal Group, 2026-08-28).
  // Every catalog id now carries a SESSION_FEE_POLICY class (constants.js), and we
  // strip STUDIO sessions only when the job is PROVABLY session-free: it contains
  // standalone (all-in) capture work and NO array capture and NO custom/unknown
  // lines — custom lines may be rig work, so they always keep the session and let
  // the operator decide. Add-ons (travel, processing, setup) are neutral. A
  // session-only quote (studio time booked, scan list TBD) is always left alone.
  // On-location day fees (partial_mobile, full_onlocation) are never auto-stripped:
  // whether they belong is a judgment call the operator makes, not the mapper.
  const policyOf = (li) => SESSION_FEE_POLICY[li.catalogId] || (li.catalogId === "custom" ? "custom" : "unknown");
  const STUDIO_SESSION_IDS = new Set(["half_art", "full_art"]);
  const policies = lineItems.map(policyOf);
  const hasArrayOrCustomCapture = policies.some((p) => p === "array_capture" || p === "custom" || p === "unknown");
  const hasStandaloneCapture = policies.some((p) => p === "standalone_capture");
  const guardedLineItems = (!hasArrayOrCustomCapture && hasStandaloneCapture)
    ? lineItems.filter((li) => !STUDIO_SESSION_IDS.has(li.catalogId))
    : lineItems;

  // Studio detection (F1): studio only when explicitly "studio", or when there's
  // no address and the model didn't flag on-location. A real address always means
  // on-location — never isStudio:true with a populated non-studio address.
  // Trim/case-tolerant ("Studio") to model drift, like the per-session check below.
  const loc = (parsed.job_location || "").trim();
  const isStudioJob = !parsed.is_onlocation && (loc.toLowerCase() === "studio" || loc === "");

  const rawSessions = Array.isArray(parsed.sessions) ? parsed.sessions : [];
  const hydratedSessions = rawSessions.map((s) => {
    const sessionType = SESSION_TYPES.includes(s?.session_type) ? s.session_type : DEFAULT_SESSION_TYPE;
    // Per-session location: an explicit address wins; "studio" flags in-studio;
    // blank inherits the job-level decision so a single-venue shoot needn't
    // repeat the address on every day. Studio days get the full structured
    // studioPatch() shape (not a bare isStudio flag) so quote→job carry-forward
    // doesn't overwrite the studio address with empty fields (F1). Mirrors the
    // job-level studio rule above.
    const rawLoc = (s?.location || "").trim();
    let locationPatch;
    if (rawLoc) {
      locationPatch = rawLoc.toLowerCase() === "studio" ? studioPatch() : { ...clearLocationPatch(), location: rawLoc };
    } else {
      locationPatch = isStudioJob ? studioPatch() : { ...clearLocationPatch(), location: loc };
    }
    return { ...BLANK_SESSION(), sessionType, date: s?.date || "", notes: s?.notes || "", ...locationPatch };
  });
  const sortedSessions = sortSessions(hydratedSessions);
  const multiDay = !!parsed.multi_day && sortedSessions.length > 1;
  // A multi-day brief with N capture sessions and no stated LiDAR day count is N capture
  // days for the estimator (the sessions came from the same brief). Stated days win. Only
  // for a SINGLE block: with several blocks each is its own scope, usually its own day, so
  // the session count says nothing about any one of them (9/12: four blocks, four days,
  // one day each).
  if (lidarPrefill && lidarPrefill.length === 1 && lidarPrefill[0].days == null && multiDay) {
    const captureDays = sortedSessions.filter((s) => s.sessionType === "capture").length;
    if (captureDays > 1) lidarPrefill = [{ ...lidarPrefill[0], days: Math.min(30, captureDays) }];
  }
  const firstDate = sortedSessions.find((s) => s.date)?.date || "";
  // The blocks this extraction creates (2026-09-04): one per LiDAR block in brief order
  // ("Fresh Kills" with its heading note "Wednesday, October 7"), then any group the
  // suggested lines carry, then every block the quote already had (a hand-made block
  // survives a re-extraction, notes intact; the operator removes what they no longer
  // want with the x). A block with no line yet is still a block, so the operator sees
  // the whole structure before the estimator walk fills it. A note the operator had
  // typed on a same-named block survives when the brief gives none.
  const prevGroups = quoteLineGroups(quote);
  // Block identity is case-insensitive (resolveGroupLabel): a brief block or a suggested
  // line's group that names a block the quote already has, in any casing, takes that
  // block's label, on the prefill and on the lines too, so a re-extraction never leaves
  // "Pile" beside a hand-made "pile" (GO review, 2026-09-04).
  // The canonical label is assigned unconditionally: canon() already returns the cleaned
  // typed value when nothing matches, so a label is cleaned and matched in one step.
  const canon = (label) => resolveGroupLabel(prevGroups, label);
  if (lidarPrefill) lidarPrefill = lidarPrefill.map((b) => (b.label ? { ...b, label: canon(b.label) } : b));
  const lineItemsOut = guardedLineItems.map((li) => (li.group ? { ...li, group: canon(li.group) } : li));
  const prevNote = (label) => prevGroups.find((g) => g.label === canon(label))?.note || "";
  const lineGroups = normalizeLineGroups([
    ...(lidarPrefill || []).filter((b) => b.label).map((b) => ({ label: b.label, note: b.note || prevNote(b.label) })),
    ...lineItemsOut.filter((li) => li.group).map((li) => ({ label: li.group, note: prevNote(li.group) })),
    ...prevGroups,
  ]);
  const lineGroupNotes = lineGroupNotesFrom(lineGroups);
  const lidarDetected = !!parsed.lidar_detected || hadVehicle || !!lidarPrefill;
  // The prefill the quote keeps: this reply's blocks, else the quote's earlier ones
  // unless the model reports no LiDAR.
  const lidarPrefillOut = lidarPrefill || (parsed.lidar_detected === false ? null : normalizeLidarBlocks(quote.lidarPrefill)) || null;

  // Quote discount from YOUR NOTES (2026-10-09): computed in code against the lines
  // just priced at catalog / tier rates. Absent → quoteDiscounts untouched (as before).
  // LiDAR still to be priced includes what the quote already flagged or keeps from an
  // earlier run, unless this reply reports no LiDAR.
  const lidarPending = lidarDetected || !!lidarPrefillOut || (!!quote.lidarDetected && parsed.lidar_detected !== false);
  // A LiDAR or drone estimator row the model suggested (dropped above, never priced
  // here) is scope still to be priced too.
  const estimatorRowDropped = rawItems.some((si) => isEstimatorCatalogId(si.catalog_id));
  const discount = resolveAIStartDiscount(parsed, {
    lineItems: lineItemsOut, quote, notesText, notesTrusted, lidarDetected: lidarPending || estimatorRowDropped,
  });

  // ── Negotiated terms & overrides (2026-08-28) ──────────────────────────────
  // All evidence-grounded and additive: an omitted/null field leaves whatever the
  // quote already has (initial extraction starts from a blank quote, so defaults
  // apply). Free text is em-dash-stripped and capped so an oversized model reply
  // can't bloat the quote doc (draft-autosave incident rule, 2026-08-21).
  // "none" from initial extraction means "no deposit stated" — treat as absent so
  // the blank quote's default (or whatever the operator set) survives.
  const parsedDeposit = sanitizeDeposit(parsed.deposit_type, parsed.deposit_value);
  const depositFromParsed = parsedDeposit && parsedDeposit.type !== "none" ? parsedDeposit : null;
  const parsedFormats = Array.isArray(parsed.deliverable_formats)
    ? parsed.deliverable_formats.filter((f) => VALID_FORMAT_IDS.has(f))
    : [];
  let sections = applyTravelRule(quote.sections || initSections(), multiDay ? sortedSessions : [{ isStudio: isStudioJob }]);
  if (parsed.data_delivery_note) {
    sections = { ...sections, dataDelivery: { ...(sections.dataDelivery || {}), enabled: true, text: capText(parsed.data_delivery_note) } };
  }
  if (parsed.project_terms) {
    sections = { ...sections, projectTerms: { ...(sections.projectTerms || {}), enabled: true, text: capText(parsed.project_terms) } };
  }
  if (parsed.no_processing) {
    // Capture-only scope: no turnaround promise, no model-format boilerplate.
    sections = {
      ...sections,
      turnaround: { ...(sections.turnaround || {}), enabled: false },
      fileFormats: { text: SECTION_DEFS.fileFormats.defaultText, ...(sections.fileFormats || {}), enabled: false },
    };
  }

  const applied = {
    ...quote,
    step: "build",
    clientName: parsed.client_name || quote.clientName,
    contactName: parsed.contact_name || quote.contactName,
    // A studio job carries the studio address, exactly as the builder's studio checkbox
    // writes it (handleStudio). It used to be written blank, so the PDF printed
    // "Location: TBD" until the operator toggled the box off and on (2026-09-04).
    jobLocation: isStudioJob ? STUDIO_ADDRESS : loc,
    isStudio: isStudioJob,
    scanDate: multiDay ? firstDate : (parsed.scan_date || firstDate),
    scanDateTBD: !!parsed.scan_date_tbd,
    multiDay,
    sessions: multiDay ? sortedSessions : [],
    // Drive the travel clause off the same sessions we persist, so the printable
    // travel terms match the Session Dates "travel enabled" note (F2). persistSessions
    // does this on every manual edit; AI apply must too, or it diverges. Single-day
    // persists no sessions, so synthesize one from the job-level studio state — a
    // one-day shoot at a client's site still needs the travel terms on the PDF.
    sections,
    summary: parsed.summary || "",
    // Visible-text only (prompt rule above); lands in an editable builder field,
    // so it is a suggestion the operator reviews, never a silent fact. Absent →
    // whatever the operator already typed survives.
    productionName: parsed.production_name || quote.productionName || "",
    docType: parsed.doc_type || "quote",
    industry: parsed.industry_guess || "other",
    turnaround: parsed.turnaround || "3-5",
    deliverableText: parsed.deliverable_text || quote.deliverableText,
    deliverableFormats: parsedFormats.length ? parsedFormats : quote.deliverableFormats,
    deposit: depositFromParsed || quote.deposit,
    // Negotiated custom terms mean the boilerplate Camera Array rate-sheet addendum
    // would print conflicting standard rates next to them — suppress the default.
    // An operator can still re-check the box in the builder.
    includePricingSheet: parsed.project_terms ? false : quote.includePricingSheet,
    leadSource: parsed.lead_source_guess || quote.leadSource,
    lineItems: lineItemsOut,
    contactMatches: buildContactMatches(parsed.extracted_contacts || [], contacts, clients),
    aiNotes: [parsed.notes || "", discount.notice].filter(Boolean).join("\n"),
    aiQuestions: parsed.questions_for_user || [],
    ...(discount.quoteDiscounts ? { quoteDiscounts: discount.quoteDiscounts } : {}),
    // LiDAR handoff: the AI may never price LiDAR (its line items are filtered above), so
    // when it detects LiDAR scope this flag drives a builder banner that routes the
    // operator to the LiDAR estimator instead of silently quoting something else.
    lidarDetected: lidarPending,
    lidarPrefill: lidarPrefillOut,
    lineGroups,
    lineGroupNotes,
  };
  return { quote: applied, discountNotice: discount.notice };
}

/* ═══ Extract job fields from a conversation transcript ═══
   Ported verbatim from legacy nyc-suite ai.js (Docs & Files tab,
   Extract & Populate). */

const CONVO_SYSTEM_PROMPT = `You are an assistant for New York Capture LLC, a 3D scanning and photogrammetry studio in Brooklyn, NYC.

Extract job details from a conversation transcript (meeting notes, call recording transcript, email thread, etc.) and return structured JSON that maps to our job management system.

Return ONLY JSON (no markdown, no backticks). Omit any fields that are not mentioned or cannot be reasonably inferred:

{
  "clientName": "company or organization name",
  "contactName": "primary contact person name",
  "project": "short project name",
  "description": "1-3 sentence project summary — what is being scanned and why",
  "industry": "film_tv|commercial_product|music|fashion|gaming|spatial|museum_archival|visual_art|gallery|engineering_manufacturing|architecture_real_estate|education_research_institutional|medical|theatre|other",
  "captureDate": "YYYY-MM-DD (first scan day)",
  "captureDateTBD": false,
  "multiDay": false,
  "sessions": [{"date": "YYYY-MM-DD", "sessionType": "capture", "dayType": "full", "notes": ""}],
  "startTime": "HH:MM (24hr)",
  "scheduledTime": "HH:MM (24hr, call time)",
  "wrapTime": "HH:MM (24hr, expected end)",
  "location": "full address or venue name",
  "locationVenue": "venue/building name",
  "locationCity": "",
  "locationState": "",
  "isStudio": false,
  "crew": "comma-separated crew names from NYC Capture",
  "selectedEquipment": ["leo", "rtc"],
  "deliverableType": "what is being delivered (e.g. textured OBJs, point cloud, LiDAR scan)",
  "deliveryDate": "YYYY-MM-DD",
  "deliveryMethod": "box.com|WeTransfer|Client FTP|Aspera|SFTP|Google Drive|Hard Drive",
  "transport": "In Studio|Sprinter|Box Truck|Car Service|Mobile (Peter or Ethan Car)|Plane",
  "prepNotes": "logistics, on-site needs, special requirements, action items",
  "notes": "general observations, context, anything else noteworthy",
  "speakerMap": {"SPEAKER_00": "Best guess name or null", "SPEAKER_01": "Best guess name or null"}
}

EQUIPMENT IDs (use these exact IDs in selectedEquipment):
big_rig=Big Rig (full photogrammetry rig)
head_rig=Head Rig (head/face capture rig)
leo=Artec Leo (handheld structured light)
spider=Artec Space Spider II (high-res small objects)
spider_i=Artec Space Spider I (high-res small objects, Gen 1)
r5=Canon R5 (reference photography / photogrammetry)
rtc=Leica RTC 360 (large-scale environment)
drone=DJI Mavic 3E (aerial photogrammetry)
flying_fox=Flying Fox (overhead cable cam)
portal=XGrids PortalCam (gaussian splatting camera)
nimble=Scanology NimbleTrack Gen 2 (blue laser scanner)

NYC Capture crew roster: Peter, Ethan, Ivin, Jason, Sam

RULES:
- Match the day-count mentioned in the transcript. Never invent sessions the transcript didn't mention.
- Single day → multiDay: false, one session entry, captureDate filled.
- Multiple days → multiDay: true, one session per day, captureDate = first session date.
- Dates vague ("mid-June", "after the opening", "sometime next month") → leave captureDate and session date empty, set captureDateTBD: true. Still populate the session count from the transcript.
- sessionType values: "capture" (scanning, shoot, photo day), "setup" (load in, prep, advance day), "strike" (teardown, wrap, load out), "flight" (travel day, fly in/out). Default to "capture" if unclear.
- Never add a strike/setup/flight session unless the transcript mentions it.
- If a date is mentioned as a contingency/rain day, note it in prepNotes, don't add a session for it.
- For equipment, map mentions to the closest equipment ID. If they mention "photogrammetry" with a camera, include r5. If they mention LiDAR/laser scanner, include rtc. Drones = drone. Artec Leo = leo.
- prepNotes should capture logistics: crowd control needs, storage space, power needs, lift/access requirements, weather concerns, parking, security, etc.
- notes should capture higher-level context: project background, client relationship notes, future opportunities, etc.
- Be thorough with prepNotes — this is the most actionable field for job preparation.
- If the conversation mentions delivery timeline in relative terms (e.g. "the following week"), calculate the actual date if possible.

SPEAKER IDENTIFICATION (speakerMap):
- If the transcript has speaker labels like [SPEAKER_00], [SPEAKER_01], etc., return a speakerMap that maps each label to your best guess of who that person is.
- Use context clues: someone who says "my business" or mentions Brooklyn/NYC Capture is likely Ivin. Someone asking about on-site needs is likely the client contact. Someone handling logistics/scheduling may be a project manager.
- NYC Capture crew roster: Peter, Ethan, Ivin, Jason, Sam. If a speaker discusses NYC Capture operations from a first-person perspective, they are likely crew.
- Set the value to null if you genuinely cannot determine who a speaker is.
- "Leo" and "Lio" are the Artec Leo scanner, NOT a person.`;

export async function extractJobFromConversation(transcript) {
  const text = await callClaude({
    system: todayHeader() + CONVO_SYSTEM_PROMPT,
    messages: [{ role: "user", content: transcript }],
    maxTokens: 3000,
  });

  return parseClaudeJSON(text);
}
